import { action, makeObservable, observable } from 'mobx';
import { Breakpoint } from '@philips-internal/oswald-ui-constants';
import { GLOBAL_LOCALE, LocaleService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import Engine, { MetadataStore } from '@philips-internal/oswald-ui-engine';
import { PREFERENCES_DIALOG_ID } from '../constants/dialogs.js';
import EditorAPI, { GLOBAL } from '../editor-api.js';
import { getEditorFileSystem, hasEditorFileSystem } from '../native/editor-file-system.js';
import { FragmentStore } from './fragment-store.js';
import { PaletteState } from './palette-state.js';
import { SidebarState } from './sidebar-state.js';
import { State } from './state.js';
import { TreeState } from './tree-state.js';
const LEFT_SIDEBAR = 'osw-left-sidebar';
const RIGHT_SIDEBAR = 'osw-right-sidebar';
const PALETTE = 'osw-palette';
const FRAGMENT_STORE = 'osw-frag-store';
var ApplicationMode;
(function (ApplicationMode) {
    ApplicationMode["EDITING"] = "editing";
    ApplicationMode["PREVIEWING"] = "previewing";
})(ApplicationMode || (ApplicationMode = {}));
class ApplicationState extends State {
    constructor(renderer) {
        super();
        Object.defineProperty(this, "applicationMode", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: ApplicationMode.EDITING
        });
        Object.defineProperty(this, "breakpoint", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: Breakpoint.GLOBAL
        });
        Object.defineProperty(this, "engine", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "locale", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: GLOBAL_LOCALE
        });
        Object.defineProperty(this, "metadataStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new MetadataStore()
        });
        Object.defineProperty(this, "metadataTree", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new TreeState()
        });
        Object.defineProperty(this, "renderer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "resolution", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: { height: 1080, width: 1920 }
        });
        Object.defineProperty(this, "showLines", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: true
        });
        makeObservable(this, {
            applicationMode: observable,
            apply: action.bound,
            breakpoint: observable,
            getLocale: action.bound,
            locale: observable,
            resolution: observable,
            setApplicationMode: action.bound,
            setBreakpoint: action.bound,
            setLocale: action.bound,
            setResolution: action.bound,
            showLines: observable,
            toggleShowLines: action.bound,
        });
        this.renderer = new renderer(this);
        this.engine = new Engine({
            metadataStore: this.metadataStore,
            renderer: this.renderer,
        });
        this.initStates();
        void this.loadFragmentControllersPath();
    }
    get preferences() {
        return this.getState(PREFERENCES_DIALOG_ID);
    }
    get fragmentStore() {
        return this.getState(FRAGMENT_STORE);
    }
    get leftSidebar() {
        return this.getState(LEFT_SIDEBAR);
    }
    get rightSidebar() {
        return this.getState(RIGHT_SIDEBAR);
    }
    get palette() {
        return this.getState(PALETTE);
    }
    async loadFragmentControllersPath() {
        if (!hasEditorFileSystem()) {
            return;
        }
        const editorFileSystem = getEditorFileSystem();
        const { collectFragmentControllers } = editorFileSystem.codegen;
        const { ensureRuntimeConfiguration } = editorFileSystem.configuration;
        const { content: configuration } = await ensureRuntimeConfiguration();
        const directory = configuration.code.controllersDirectory;
        const fragmentControllers = await collectFragmentControllers(directory);
        this.fragmentStore.loadFragmentControllersPath(fragmentControllers);
    }
    initStates() {
        this.addState(LEFT_SIDEBAR, new SidebarState());
        this.addState(RIGHT_SIDEBAR, new SidebarState());
        this.addState(PALETTE, new PaletteState());
        this.addState(FRAGMENT_STORE, new FragmentStore(this));
        EditorAPI.dialogs.items()
            .filter(item => (!item.scope || item.scope.includes(GLOBAL))).forEach(({ id, state }) => {
            state && this.addState(id, new state(this));
        });
    }
    setApplicationMode(applicationMode = ApplicationMode.EDITING) {
        this.applicationMode = applicationMode;
    }
    isEditing() {
        return this.applicationMode === ApplicationMode.EDITING;
    }
    toggleShowLines() {
        this.showLines = !this.showLines;
    }
    getLocale() {
        return this.locale;
    }
    setLocale(locale) {
        this.locale = locale;
        LocaleService.setCurrentLocale(locale);
    }
    getBreakpoint() {
        return this.breakpoint;
    }
    setBreakpoint(breakpoint) {
        this.breakpoint = breakpoint;
    }
    setResolution(resolution) {
        this.resolution = resolution;
    }
    getResolution() {
        return this.resolution;
    }
    getFocusedFragment() {
        const fragmentStore = this.fragmentStore;
        return fragmentStore.getFocused();
    }
    collect() {
        const statesData = super.collect();
        return {
            applicationMode: this.applicationMode,
            breakpoint: this.breakpoint,
            locale: this.locale,
            resolution: this.resolution,
            showLines: this.showLines,
            ...statesData,
        };
    }
    apply(data) {
        const { applicationMode, breakpoint, locale, resolution, showLines, ...otherData } = data;
        this.applicationMode = applicationMode ?? this.applicationMode;
        this.breakpoint = breakpoint ?? this.breakpoint;
        this.resolution = resolution ?? this.resolution;
        this.showLines = showLines ?? this.showLines;
        // Do it like this to also sync LocaleService.
        this.setLocale(locale || this.locale);
        super.apply(otherData);
    }
}
export { ApplicationMode, ApplicationState, };
//# sourceMappingURL=application-state.js.map